import {
  addTransactionSignature,
  deleteTransactionSignature,
  setSignatureRequirementMode,
} from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import {
  buildEmptyScellement,
  buildEmptySignature,
} from "@/state/transactions/CurrentTransactionStore";
import {
  File,
  Signatory,
  Signature,
} from "@/type/core/dashboard/dashboard.types";
import {
  SignatureRequirementMode,
  TransactionDetail,
  TransactionType,
} from "@/type/core/transaction/transaction.types";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import { authenticationStore } from "@state/auth/AuthStore";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { SignaturePicturePriorityEnum } from "@utils/enums/signature.picture.priority.enum";
import { allSignatoriesHaveAtLeastOneSignature } from "@utils/helpers/signatories-helper";
import SignatureHelper from "@utils/helpers/signature-helper";
import { TransactionHelper } from "@utils/helpers/transaction-helper";
import { useUnit } from "effector-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

interface UseSignaturePositioningReturn {
  transactionType: TransactionType;
  files: File[];
  signatories: Signatory[];
  signatures: Signature[];
  signatureRequirementMode: SignatureRequirementMode;
  setSignatureRequirementMode: (
    signatureRequirementMode: SignatureRequirementMode,
  ) => void;
  manualPositioning: boolean;
  setManualPositioning: Dispatch<SetStateAction<boolean>>;
  officeId?: string;
  transactionDetail?: TransactionDetail;
}

const useSignaturePositioning = (): UseSignaturePositioningReturn => {
  const {
    transactionType,
    files,
    signatories,
    transactionForm,
    signatures,
    isSignaturesFromModel,
    isFromDraft,
    transactionDetail,
    signatureRequirementMode,
  } = useCurrentTransaction();
  const officeId = transactionForm.office;
  const [manualPositioning, setManualPositioning] = useState<boolean>(false);

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const office = authStore?.user?.offices.find(
    (office) => office.id === officeId,
  );

  const currentTransactionSignatureRequirementMode =
    transactionDetail?.signatureRequirementMode;

  const isRequirementModeALL =
    !currentTransactionSignatureRequirementMode ||
    currentTransactionSignatureRequirementMode === "ALL";
  const hasAnySignature = signatures.length > 0;
  const allSignatoriesHaveOneSignature = allSignatoriesHaveAtLeastOneSignature(
    signatories,
    signatures,
  );
  const eachOnEachDoc =
    TransactionHelper.eachSignatoryHasOnSignatureOnEachDocument(
      signatories.length,
      files.length,
      signatures.length,
    );

  useEffect(() => {
    if (
      isFromDraft &&
      isRequirementModeALL &&
      hasAnySignature &&
      allSignatoriesHaveOneSignature &&
      eachOnEachDoc
    ) {
      return;
    }
    if (transactionType === "signature") {
      if (files.length < 1 || signatories.length < 1) {
        return;
      }
      files.forEach((_, fileIndex) => {
        signatories.forEach((signatory, indexSignatory) => {
          let signature = undefined;
          const rank = fileIndex + 1;

          if (isSignaturesFromModel) {
            signature = signatures.find(
              (signature) => signature.email === signatory.email,
            );
          } else if (isFromDraft) {
            signature = signatures.find(
              (signature) =>
                signature.email === signatory.email && signature.rank === rank,
            );
          }

          const signaturePicture = signatory.signaturePicture?.filter(
            (signaturePicture) => signaturePicture.isDefault,
          )[0];
          const defaultSignaturePictureRank =
            (signaturePicture && signaturePicture.rank) ??
            signaturePicture?.rank;

          const email = signature?.email ?? signatory.email;
          const pattern =
            signature?.pattern ??
            signatory.pattern ??
            SignaturePatternEnum.DEFAULT;
          const signaturePictureRank =
            signature?.signaturePictureRank ?? defaultSignaturePictureRank;
          const signaturePriorityRule =
            signature?.signaturePriorityRule ?? signatory.signaturePriority;
          const name =
            signature?.name ?? `${signatory.firstname} ${signatory.lastname}`;
          const positionX = signature?.positionX ?? 0;
          const positionY = signature?.positionY ?? 0;
          const page = signature?.page ?? 0;
          const isMoved = signature?.isMoved ?? false;
          const identificator = SignatureHelper.buildSignatureIdentificator(
            fileIndex,
            indexSignatory,
            0, // It is the first signature
          );

          const newSignature = buildEmptySignature({
            email,
            rank,
            pattern,
            signaturePictureRank,
            signaturePriorityRule,
            name,
            positionX,
            positionY,
            page,
            isMoved,
            identificator,
          });

          if (isSignaturesFromModel) {
            const prepositionedSignature = signatures.find(
              (s) => s.email === email,
            );
            if (prepositionedSignature) {
              deleteTransactionSignature(prepositionedSignature);
              SignatureHelper.createPairOfSignature(
                prepositionedSignature.isMoved ?? false,
                newSignature,
                fileIndex,
                indexSignatory,
              );
              return;
            }
          }

          addTransactionSignature(newSignature);
        });
      });
    } else {
      let positionX: number | undefined = undefined;
      let positionY: number | undefined = undefined;
      let page: number | undefined = undefined;
      let isMoved: boolean | undefined = undefined;
      const signaturePriorityRule =
        office?.scellementPriority === "scellement_picture"
          ? SignaturePicturePriorityEnum.SCELLEMENT_PICTURE
          : SignaturePicturePriorityEnum.NO_PICTURE;
      const identificator = SignatureHelper.buildSignatureIdentificator(
        0,
        0,
        0,
      );
      const signaturePictureRank =
        office?.scellementPriority === "scellement_picture" ? 1 : 0;

      if (signatures.length > 0) {
        const signature = signatures[0];
        positionX = signature?.positionX ?? 0;
        positionY = signature?.positionY ?? 0;
        page = signature?.page ?? 0;
        isMoved = signature?.isMoved ?? false;
      }

      addTransactionSignature(
        buildEmptyScellement({
          positionX,
          positionY,
          page,
          isMoved,
          signaturePriorityRule,
          identificator,
          signaturePictureRank,
        }),
      );
    }
    // }, [files, signatories, transactionType, isSignaturesFromModel]);
  }, []);

  useEffect(() => {
    if (currentTransactionSignatureRequirementMode) {
      setSignatureRequirementMode(currentTransactionSignatureRequirementMode);
    }
  }, [currentTransactionSignatureRequirementMode]);

  return {
    transactionType,
    files,
    signatories,
    signatures,
    signatureRequirementMode,
    setSignatureRequirementMode,
    manualPositioning,
    setManualPositioning,
    officeId,
    transactionDetail,
  };
};

export default useSignaturePositioning;
